import PropTypes from 'prop-types';
import React from 'react';
import cn from 'classnames';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { CSSTransition } from 'react-transition-group';
import { injectIntl, FormattedNumber } from 'react-intl';
import { bindAll } from '@bibliocommons/utils-react';
import * as Icons from '@bibliocommons/deprecated-base-icons';
import CheckboxFilter from '../CheckboxFilter';
import ExpandFilterLink from '../ExpandFilterLink';
import LinkFilter from '../LinkFilter';
import './SimpleField.scss';

const VISIBLE_FILTERS_LIMIT = 15;

const propTypes = {
  group: PropTypes.string,
  initialIsExpanded: PropTypes.bool,
  isConjunctive: PropTypes.bool,
  fieldId: PropTypes.string,
  filters: ImmutablePropTypes.list,
  hasMore: PropTypes.bool,
  activeFilters: PropTypes.object,
  intl: PropTypes.object,
  icon: PropTypes.string,
  iconName: PropTypes.string,
  handleToggleFilter: PropTypes.func,
  label: PropTypes.string
};

export class SimpleField extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isExpanded: this.props.initialIsExpanded
    };

    bindAll(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isExpanded && this.state.isExpanded && this._firstFilter && this._firstFilter._focusableElement) {
      this._firstFilter._focusableElement.focus();
    }
  }

  expandField(e) {
    if (e) {
      e.preventDefault();
    }
    this.setState(state => ({ isExpanded: !state.isExpanded }));
  }

  static renderExpandIcon(expanded) {
    if (expanded) {
      return <Icons.Arrows.Medium.Up color="#333" className="small" />;
    }

    return <Icons.Arrows.Medium.Down color="#333" className="small" />;
  }

  static renderLabel(filterLabel, filter) {
    return (
      <span className="filter-label-text">
        {filterLabel}
        <span className="filter-count">
          (<FormattedNumber value={filter.get('count')} />)
        </span>
      </span>
    );
  }

  renderFilter(filter, index) {
    const { fieldId, isConjunctive, group } = this.props;
    const FilterTag = isConjunctive ? LinkFilter : CheckboxFilter;
    const value = filter.get('value');
    const setFirstFilter = el => {
      if (index === 0) {
        this._firstFilter = el;
      }
    };

    return (
      <li key={`${fieldId}-${group}-${value}`} className={cn('filter', { applied: filter.get('applied') })}>
        <FilterTag
          group={group}
          fieldId={this.props.fieldId}
          filter={filter}
          handleToggleFilter={this.props.handleToggleFilter}
          refFunction={setFirstFilter}
        />
      </li>
    );
  }

  renderExpandFilterLink() {
    const hasMore = this.props.hasMore || this.props.filters.size > VISIBLE_FILTERS_LIMIT;
    if (hasMore && this.state.isExpanded) {
      return <ExpandFilterLink {...this.props} />;
    }
    return null;
  }

  renderIcon() {
    if (this.props.iconName) {
      const IconComponent = Icons[this.props.iconName];

      return <IconComponent className="flip-vert" color="#333" />;
    }
    return <span aria-hidden className={this.props.icon} />;
  }

  render() {
    const { intl, label, fieldId, filters } = this.props;
    return (
      <section className={`cp-simple-field clearfix ${fieldId} `}>
        <a
          href="#expand-filter"
          data-key={`${fieldId}-expand-filter`}
          onClick={this.expandField}
          className="field-toggle"
          aria-expanded={this.state.isExpanded}
        >
          {this.renderIcon()}
          <span className="field-name">{intl.formatMessage({ id: label })}</span>
          {SimpleField.renderExpandIcon(this.state.isExpanded)}
        </a>
        <CSSTransition
          in={this.state.isExpanded}
          classNames="fade"
          timeout={{ enter: 150, exit: 75 }}
          mountOnEnter
          unmountOnExit
        >
          <ul className="filter-list">
            {filters
              .filter((f, index) => index < VISIBLE_FILTERS_LIMIT || f.get('applied'))
              .valueSeq()
              .map(this.renderFilter)}
          </ul>
        </CSSTransition>
        {this.renderExpandFilterLink()}
      </section>
    );
  }
}

SimpleField.propTypes = propTypes;

export default injectIntl(SimpleField);
